import {Stack, Typography, Button} from '@mui/material';
import {IconIllustration, CTAButtonFixed} from 'presentation/components';
import React from 'react';
import {ResponsiveCard} from '../ResponsiveCard';
import {useCallChatRoom, useMediaQueries} from 'presentation/pages/private';
import {useLocation, useNavigate} from 'react-router-dom';
import {FacialBiometricEnum, ChatRoomsEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {ErrorCardProps} from './types';
import {DIVERGENT_PHOTOS_STATUS_LIST} from '../DocumentUpload/constants';
import Icon from 'presentation/icons';
import {useDivergentAttemptsStore} from '../../store';
import {MAX_DIVERGENT_ATTEMPTS} from './constants';
import {CustomAlert} from '../CustomAlert';

const {errorCard} = translations['pt-br'].others.facialBiometric;

export function ErrorCard({errorCode, onRetry, onResend}: ErrorCardProps) {
	const {isTablet} = useMediaQueries();
	const navigate = useNavigate();
	const location = useLocation();
	const {divergentAttempts, incrementAttempts} = useDivergentAttemptsStore();
	const {callChatRoom} = useCallChatRoom();
	const isDivergentPhotosError = errorCode && DIVERGENT_PHOTOS_STATUS_LIST.includes(errorCode);
	const config = getErrorConfig(divergentAttempts);

	const showAlert = isDivergentPhotosError && divergentAttempts < MAX_DIVERGENT_ATTEMPTS;

	function getErrorConfig(attempts: number) {
		if (isDivergentPhotosError) {
			if (attempts >= MAX_DIVERGENT_ATTEMPTS) {
				return {
					title: errorCard.stillDivergentInfo.title,
					description: errorCard.stillDivergentInfo.description,
				};
			}

			return {
				title: errorCard.divergentInfo.title,
				description: errorCard.divergentInfo.description,
			};
		}

		return {
			title: errorCard.loadError.title,
			description: errorCard.loadError.description,
			hasTryAgainButton: true,
		};
	}

	function handleResend() {
		if (isDivergentPhotosError) {
			incrementAttempts();
			navigate({pathname: FacialBiometricEnum.FACE_PHOTO, search: location.search});
		} else {
			onResend();
		}
	}

	const hasMaxDivergentAttempts = divergentAttempts >= MAX_DIVERGENT_ATTEMPTS;

	async function primaryAction() {
		if (config.hasTryAgainButton) {
			onRetry();
			return;
		}

		if (isDivergentPhotosError && hasMaxDivergentAttempts) {
			await callChatRoom(ChatRoomsEnum.SAC);
			return;
		}

		handleResend();
	}

	const primaryLabel = (() => {
		if (config.hasTryAgainButton) return errorCard.retry;

		if (isDivergentPhotosError && hasMaxDivergentAttempts) {
			return errorCard.talkToSupport;
		}
		return errorCard.retryRegister;
	})();

	return (
		<>
			<ResponsiveCard>
				<Stack
					alignItems="center"
					gap={2}
					textAlign="center"
					justifyContent="center"
					pt={8}
					sx={{
						height:
							isTablet && !config?.hasTryAgainButton ? 'calc(100% - 98px)' : '100%',
					}}>
					<IconIllustration variant="info" customIconName="info-filled" />
					<Typography variant="h6">{config.title}</Typography>
					<Typography variant="body1">{config.description}</Typography>

					{config.hasTryAgainButton && (
						<Button
							onClick={primaryAction}
							variant="outlined"
							sx={{marginBottom: '64px'}}
							endIcon={<Icon name="reload" />}>
							{primaryLabel}
						</Button>
					)}
					{showAlert && (
						<Stack width="100%" sx={{marginTop: '64px'}}>
							<CustomAlert message={errorCard.divergentInfo.alert} />
						</Stack>
					)}
					{!isTablet && !config?.hasTryAgainButton && (
						<Stack gap={2} alignItems="center" width="100%">
							<Stack direction="row">
								<Button onClick={primaryAction}>{primaryLabel}</Button>
							</Stack>
						</Stack>
					)}
				</Stack>
			</ResponsiveCard>

			{isTablet && !config?.hasTryAgainButton ? (
				<CTAButtonFixed.Root $isTablet={isTablet} sx={{bottom: 0}}>
					<CTAButtonFixed.Action onClick={primaryAction} fullWidth>
						{primaryLabel}
					</CTAButtonFixed.Action>
				</CTAButtonFixed.Root>
			) : null}
		</>
	);
}
